import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users,
  TrendingUp,
  DollarSign,
  Truck,
  ArrowUpRight,
  Search,
  Filter,
  Eye,
  Star,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Summary Stats Card Component
function StatCard({ title, value, subtitle, icon: Icon, bgColor, iconColor }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && (
              <div className="flex items-center mt-2 text-sm text-green-600">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                {subtitle}
              </div>
            )}
          </div>
          <div className={`p-3 ${bgColor} rounded-full`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Driver Row Component
function DriverRow({ driver, onView }) {
  const metrics = driver.metrics || {};
  const completionRate = metrics.completion_rate || 0;
  
  return (
    <tr className="hover:bg-slate-50" data-testid={`driver-row-${driver.id}`}>
      <td className="px-6 py-4">
        <div>
          <p className="font-medium text-slate-900">{driver.name}</p>
          <p className="text-sm text-slate-500">{driver.email}</p>
        </div>
      </td>
      <td className="px-6 py-4">
        <Badge variant={driver.is_available ? 'default' : 'secondary'}>
          {driver.is_available ? 'Online' : 'Offline'}
        </Badge>
      </td>
      <td className="px-6 py-4 text-right">
        <span className="font-medium">{metrics.total_deliveries || 0}</span>
      </td>
      <td className="px-6 py-4 text-right">
        <span className="font-medium text-green-600">
          £{(metrics.total_earnings || 0).toFixed(2)}
        </span>
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${
                completionRate >= 90 ? 'bg-green-500' : completionRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${completionRate}%` }}
            />
          </div>
          <span className="text-sm">{completionRate}%</span>
        </div>
      </td>
      <td className="px-6 py-4 text-right">
        <div>
          <p className="font-medium">{metrics.today_deliveries || 0}</p>
          <p className="text-sm text-green-600">£{(metrics.today_earnings || 0).toFixed(2)}</p>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onView(driver)}
          data-testid={`view-driver-${driver.id}`}
        >
          <Eye className="w-4 h-4 mr-1" />
          View
        </Button>
      </td>
    </tr>
  );
}

export default function DriverPerformance() {
  const [summary, setSummary] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDetails, setDriverDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, driversRes] = await Promise.all([
        axios.get(`${API}/api/admin/drivers/performance/summary`, { headers }),
        axios.get(`${API}/api/admin/drivers`, { headers }),
      ]);

      setSummary(summaryRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      console.error('Failed to fetch driver data:', error);
      toast.error('Failed to load driver performance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetails = async (driverId) => {
    try {
      setDetailsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/drivers/${driverId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDriverDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch driver details:', error);
      toast.error('Failed to load driver details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    fetchDriverDetails(driver.id);
  };

  const filteredDrivers = drivers.filter((driver) => {
    const nameMatch = driver.name ? driver.name.toLowerCase().includes(searchQuery.toLowerCase()) : false;
    const emailMatch = driver.email ? driver.email.toLowerCase().includes(searchQuery.toLowerCase()) : false;
    const matchesSearch = nameMatch || emailMatch;
    
    let matchesStatus = statusFilter === 'all';
    if (statusFilter === 'active') matchesStatus = driver.is_available === true;
    if (statusFilter === 'inactive') matchesStatus = driver.is_available === false;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  // Extract summary data for simpler access
  const driversData = summary ? summary.drivers : { total: 0, active: 0, inactive: 0 };
  const deliveriesData = summary ? summary.deliveries : { today: 0, this_week: 0, total: 0 };
  const earningsData = summary ? summary.earnings : { today: 0, this_week: 0, total: 0 };
  const topPerformers = summary ? summary.top_performers : [];

  return (
    <div className="space-y-6" data-testid="driver-performance-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Driver Performance</h1>
          <p className="text-slate-500 mt-1">Monitor and analyze driver metrics</p>
        </div>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-btn">
          <Activity className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Drivers"
            value={driversData.total}
            subtitle={`${driversData.active} active | ${driversData.inactive} offline`}
            icon={Users}
            bgColor="bg-blue-50"
            iconColor="text-blue-600"
          />
          <StatCard
            title="Today&apos;s Deliveries"
            value={deliveriesData.today}
            subtitle={`${deliveriesData.this_week} this week`}
            icon={Truck}
            bgColor="bg-green-50"
            iconColor="text-green-600"
          />
          <StatCard
            title="Today&apos;s Earnings"
            value={`£${earningsData.today.toFixed(2)}`}
            subtitle={`£${earningsData.this_week.toFixed(2)} this week`}
            icon={DollarSign}
            bgColor="bg-orange-50"
            iconColor="text-orange-600"
          />
          <StatCard
            title="Total Earnings"
            value={`£${earningsData.total.toFixed(2)}`}
            subtitle={`${deliveriesData.total} total deliveries`}
            icon={TrendingUp}
            bgColor="bg-purple-50"
            iconColor="text-purple-600"
          />
        </div>
      )}

      {/* Top Performers */}
      {topPerformers.length > 0 && (
        <Card data-testid="top-performers-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Top Performers This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {topPerformers.map((driver, index) => {
                const bgColors = ['bg-yellow-500', 'bg-slate-400', 'bg-amber-600', 'bg-slate-300', 'bg-slate-300'];
                return (
                  <div key={driver.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${bgColors[index] || 'bg-slate-300'}`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{driver.name}</p>
                      <p className="text-xs text-slate-500">
                        {driver.deliveries} deliveries • £{driver.earnings.toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search drivers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="driver-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Drivers</SelectItem>
            <SelectItem value="active">Active Only</SelectItem>
            <SelectItem value="inactive">Inactive Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Drivers Table */}
      <Card data-testid="drivers-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Driver</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Deliveries</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Earnings</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Completion Rate</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Today</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredDrivers.map((driver) => (
                  <DriverRow key={driver.id} driver={driver} onView={handleViewDriver} />
                ))}
                {filteredDrivers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      No drivers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Driver Details Modal */}
      <Dialog open={!!selectedDriver} onOpenChange={() => setSelectedDriver(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Driver Details
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
            </div>
          ) : driverDetails ? (
            <DriverDetailsContent details={driverDetails} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Driver Details Content Component
function DriverDetailsContent({ details }) {
  const driver = details.driver || {};
  const summaryData = details.summary || {};
  const statusBreakdown = details.status_breakdown || {};
  const recentOrders = details.recent_orders || [];

  return (
    <div className="space-y-6">
      {/* Driver Info */}
      <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
          <Users className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{driver.name}</h3>
          <p className="text-slate-500">{driver.email}</p>
          <p className="text-sm text-slate-400 mt-1">
            Phone: {driver.phone || 'Not provided'}
          </p>
        </div>
        <Badge variant={driver.is_available ? 'default' : 'secondary'}>
          {driver.is_available ? 'Online' : 'Offline'}
        </Badge>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-blue-600">{summaryData.total_orders || 0}</p>
          <p className="text-sm text-slate-600">Total Orders</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-green-600">{summaryData.completed_orders || 0}</p>
          <p className="text-sm text-slate-600">Completed</p>
        </div>
        <div className="p-4 bg-orange-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-orange-600">
            £{(summaryData.total_earnings || 0).toFixed(2)}
          </p>
          <p className="text-sm text-slate-600">Total Earnings</p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-purple-600">
            {summaryData.completion_rate || 0}%
          </p>
          <p className="text-sm text-slate-600">Completion Rate</p>
        </div>
      </div>

      {/* Status Breakdown */}
      {Object.keys(statusBreakdown).length > 0 && (
        <div>
          <h4 className="font-medium text-slate-900 mb-3">Order Status Breakdown</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusBreakdown).map(([status, count]) => (
              <Badge key={status} variant="outline" className="text-sm">
                {status}: {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Recent Orders */}
      {recentOrders.length > 0 && (
        <div>
          <h4 className="font-medium text-slate-900 mb-3">Recent Orders</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {recentOrders.slice(0, 10).map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-sm">#{order.id ? order.id.slice(0, 8) : 'N/A'}</p>
                  <p className="text-xs text-slate-500">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className="text-right">
                  <Badge
                    variant={
                      order.status === 'delivered' ? 'default' :
                      order.status === 'cancelled' ? 'destructive' : 'secondary'
                    }
                  >
                    {order.status}
                  </Badge>
                  <p className="text-sm font-medium text-green-600 mt-1">
                    £{(order.delivery_fee || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
